// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { PublicKey } from "@rialo/ts-cdk";
import { Store } from "@tanstack/store";
import type {
  IdentifierArray,
  IdentifierString,
  Wallet,
  WalletAccount,
  WalletIcon,
} from "@wallet-standard/base";
import type {
  StandardConnectFeature,
  StandardDisconnectFeature,
  StandardEventsFeature,
} from "@wallet-standard/features";
import type { FrostConfig } from "../config";
import type { AccountEntity, FrostAppState, WalletEntity } from "../types";

// =============================================================================
// Mock Wallet Account
// =============================================================================

export interface CreateMockAccountOptions {
  address?: string;
  publicKey?: Uint8Array;
  chains?: IdentifierArray;
  features?: IdentifierArray;
  label?: string;
}

let accountCounter = 0;

/**
 * Creates a mock WalletAccount for testing.
 */
export function createMockAccount(options: CreateMockAccountOptions = {}): WalletAccount {
  const id = ++accountCounter;
  const address = options.address ?? `MockAddress${id}${"X".repeat(32)}`.slice(0, 44);
  const publicKey = options.publicKey ?? new Uint8Array(32).fill(id);

  return {
    address,
    publicKey,
    chains: options.chains ?? ["rialo:devnet" as IdentifierString],
    features: options.features ?? [],
    label: options.label,
    icon: undefined,
  };
}

// =============================================================================
// Mock Wallet
// =============================================================================

export interface CreateMockWalletOptions {
  name?: string;
  icon?: string;
  chains?: IdentifierString[];
  accounts?: WalletAccount[];
  /** Auto-approve connection requests */
  autoApprove?: boolean;
  /** Delay before resolving connect (ms) */
  connectDelay?: number;
  /** Simulate connection failure */
  failConnect?: boolean | Error;
  /** Simulate disconnect failure */
  failDisconnect?: boolean | Error;
  /** Include standard:events feature */
  supportsEvents?: boolean;
  /** Include rialo:signMessage feature */
  supportsSignMessage?: boolean;
  /** Include rialo:signTransaction feature */
  supportsSignTransaction?: boolean;
  /** Include rialo:signAndSendTransaction feature */
  supportsSignAndSend?: boolean;
}

export interface MockWallet extends Wallet {
  /** Programmatically trigger account change event */
  _emitAccountChange: (accounts: WalletAccount[]) => void;
  /** Get connect call count */
  _connectCallCount: number;
  /** Get disconnect call count */
  _disconnectCallCount: number;
  /** Get sign message call count */
  _signMessageCallCount: number;
  /** Get sign transaction call count */
  _signTransactionCallCount: number;
  /** Last signed message */
  _lastSignedMessage: Uint8Array | null;
  /** Last signed transaction */
  _lastSignedTransaction: Uint8Array | null;
  /** Reset all counters and state */
  _reset: () => void;
}

let walletCounter = 0;

/**
 * Creates a mock Wallet implementing wallet-standard interface.
 */
export function createMockWallet(options: CreateMockWalletOptions = {}): MockWallet {
  const id = ++walletCounter;
  const {
    name = `MockWallet${id}`,
    icon = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'/>",
    chains = ["rialo:devnet" as IdentifierString, "rialo:testnet" as IdentifierString],
    accounts = [createMockAccount()],
    autoApprove = true,
    connectDelay = 0,
    failConnect = false,
    failDisconnect = false,
    supportsEvents = true,
    supportsSignMessage = true,
    supportsSignTransaction = true,
    supportsSignAndSend = true,
  } = options;

  // Event listeners for standard:events
  type EventType = "change";
  type ChangeEvent = { accounts?: readonly WalletAccount[] };
  const eventListeners = new Map<EventType, Set<(event: ChangeEvent) => void>>();

  // Internal state
  let connectCallCount = 0;
  let disconnectCallCount = 0;
  let signMessageCallCount = 0;
  let signTransactionCallCount = 0;
  let lastSignedMessage: Uint8Array | null = null;
  let lastSignedTransaction: Uint8Array | null = null;
  let connectedAccounts: WalletAccount[] = [];

  // Build features object
  const features: Record<string, unknown> = {};

  // standard:connect
  const connectFeature: StandardConnectFeature["standard:connect"] = {
    version: "1.0.0" as const,
    connect: async ({ silent }: { silent?: boolean } = {}) => {
      connectCallCount++;

      if (connectDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, connectDelay));
      }

      if (failConnect) {
        throw failConnect instanceof Error ? failConnect : new Error("Connection rejected");
      }

      if (!autoApprove && !silent) {
        throw new Error("User rejected connection");
      }

      connectedAccounts = [...accounts];
      return { accounts: connectedAccounts };
    },
  };
  features["standard:connect"] = connectFeature;

  // standard:disconnect
  const disconnectFeature: StandardDisconnectFeature["standard:disconnect"] = {
    version: "1.0.0" as const,
    disconnect: (): Promise<void> => {
      disconnectCallCount++;

      if (failDisconnect) {
        throw failDisconnect instanceof Error ? failDisconnect : new Error("Disconnect failed");
      }

      connectedAccounts = [];
      return Promise.resolve();
    },
  };
  features["standard:disconnect"] = disconnectFeature;

  // standard:events
  if (supportsEvents) {
    const eventsFeature: StandardEventsFeature["standard:events"] = {
      version: "1.0.0" as const,
      on: <E extends EventType>(event: E, listener: (event: ChangeEvent) => void): (() => void) => {
        if (!eventListeners.has(event)) {
          eventListeners.set(event, new Set());
        }
        eventListeners.get(event)?.add(listener);

        return () => {
          eventListeners.get(event)?.delete(listener);
        };
      },
    };
    features["standard:events"] = eventsFeature;
  }

  // rialo:signMessage
  if (supportsSignMessage) {
    features["rialo:signMessage"] = {
      version: "1.0.0",
      signMessage: ({
        message,
      }: {
        account: WalletAccount;
        message: Uint8Array;
      }): Promise<readonly { signature: Uint8Array; signedMessage: Uint8Array }[]> => {
        signMessageCallCount++;
        lastSignedMessage = message;

        // Create a mock signature (64 bytes for Ed25519)
        const signature = new Uint8Array(64);
        signature.fill(0xab);

        return Promise.resolve([
          {
            signature,
            signedMessage: message,
          },
        ]);
      },
    };
  }

  // rialo:signTransaction
  if (supportsSignTransaction) {
    features["rialo:signTransaction"] = {
      version: "1.0.0",
      signTransaction: ({
        transaction,
      }: {
        account: WalletAccount;
        transaction: Uint8Array;
        chain: string;
      }): Promise<readonly { signedTransaction: Uint8Array }[]> => {
        signTransactionCallCount++;
        lastSignedTransaction = transaction;

        // Return the transaction with a mock signature prepended
        const signedTransaction = new Uint8Array(transaction.length + 64);
        signedTransaction.set(new Uint8Array(64).fill(0xcd), 0);
        signedTransaction.set(transaction, 64);

        return Promise.resolve([{ signedTransaction }]);
      },
    };
  }

  // rialo:signAndSendTransaction
  if (supportsSignAndSend) {
    features["rialo:signAndSendTransaction"] = {
      version: "1.0.0",
      signAndSendTransaction: ({
        transaction,
      }: {
        account: WalletAccount;
        transaction: Uint8Array;
        chain: string;
      }): Promise<readonly { signature: Uint8Array }[]> => {
        signTransactionCallCount++;
        lastSignedTransaction = transaction;

        // Return a mock signature
        const signature = new Uint8Array(64);
        signature.fill(0xef);

        return Promise.resolve([{ signature }]);
      },
    };
  }

  const wallet: MockWallet = {
    version: "1.0.0" as const,
    name,
    icon: icon as WalletIcon,
    chains,
    features,
    accounts: connectedAccounts,

    // Test helpers
    _emitAccountChange: (newAccounts: WalletAccount[]) => {
      connectedAccounts = newAccounts;
      const listeners = eventListeners.get("change");
      if (listeners) {
        for (const listener of listeners) {
          listener({ accounts: newAccounts });
        }
      }
    },

    get _connectCallCount(): number {
      return connectCallCount;
    },

    get _disconnectCallCount(): number {
      return disconnectCallCount;
    },

    get _signMessageCallCount(): number {
      return signMessageCallCount;
    },

    get _signTransactionCallCount(): number {
      return signTransactionCallCount;
    },

    get _lastSignedMessage(): Uint8Array | null {
      return lastSignedMessage;
    },

    get _lastSignedTransaction(): Uint8Array | null {
      return lastSignedTransaction;
    },

    _reset: (): void => {
      connectCallCount = 0;
      disconnectCallCount = 0;
      signMessageCallCount = 0;
      signTransactionCallCount = 0;
      lastSignedMessage = null;
      lastSignedTransaction = null;
      connectedAccounts = [];
      eventListeners.clear();
    },
  };

  return wallet;
}

// =============================================================================
// Mock RPC Client
// =============================================================================

export interface CreateMockClientOptions {
  /** Default balance to return */
  defaultBalance?: bigint;
  /** Map of address -> balance */
  balances?: Map<string, bigint>;
  /** Simulate RPC errors */
  failRequests?: boolean | Error;
  /** Delay before resolving requests (ms) */
  requestDelay?: number;
  /** Mock transaction signatures to return */
  transactionSignature?: string;
}

/** Result of a confirmed transaction (matches @rialo/ts-cdk ConfirmedTransaction) */
export interface MockConfirmedTransaction {
  signature: string;
  executed: boolean;
  err?: string;
}

/**
 * Mock RialoClient interface for testing.
 * Only includes the methods commonly needed in tests.
 */
export interface MockRialoClient {
  // Mocked RPC methods - matches real RialoClient signature
  getBalance: (pubkey: PublicKey) => Promise<bigint>;
  sendTransaction: (transaction: Uint8Array) => Promise<string>;
  sendAndConfirmTransaction: (transaction: Uint8Array) => Promise<MockConfirmedTransaction>;
  getTransaction: () => Promise<unknown>;
  getLatestBlockhash: () => Promise<{ blockhash: string; lastValidBlockHeight: number }>;

  // Test helpers
  /** Set balance for an address (use address string as key) */
  _setBalance: (address: string, balance: bigint) => void;
  /** Get send transaction call count */
  _sendTransactionCallCount: number;
  /** Get sendAndConfirm call count */
  _sendAndConfirmCallCount: number;
  /** Last sent transaction */
  _lastSentTransaction: Uint8Array | null;
  /** Configure transaction to fail on-chain (executed: false) */
  _setTransactionFails: (fails: boolean, err?: string) => void;
  /** Reset all state */
  _reset: () => void;
}

/**
 * Creates a mock RialoClient for testing.
 */
export function createMockClient(options: CreateMockClientOptions = {}): MockRialoClient {
  const {
    defaultBalance = 1_000_000_000n, // 1 RLO equivalent
    balances = new Map(),
    failRequests = false,
    requestDelay = 0,
    transactionSignature = "MockTxSignature123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefgh",
  } = options;

  let sendTransactionCallCount = 0;
  let sendAndConfirmCallCount = 0;
  let lastSentTransaction: Uint8Array | null = null;
  let transactionFails = false;
  let transactionError: string | undefined;

  const maybeDelay = async (): Promise<void> => {
    if (requestDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, requestDelay));
    }
  };

  const maybeThrow = (): void => {
    if (failRequests) {
      throw failRequests instanceof Error ? failRequests : new Error("RPC request failed");
    }
  };

  const client: MockRialoClient = {
    getBalance: async (pubkey: PublicKey): Promise<bigint> => {
      await maybeDelay();
      maybeThrow();
      // Convert PublicKey to string for balance lookup
      const address = pubkey.toString();
      return balances.get(address) ?? defaultBalance;
    },

    sendTransaction: async (transaction: Uint8Array): Promise<string> => {
      await maybeDelay();
      maybeThrow();
      sendTransactionCallCount++;
      lastSentTransaction = transaction;
      return transactionSignature;
    },

    sendAndConfirmTransaction: async (
      transaction: Uint8Array
    ): Promise<MockConfirmedTransaction> => {
      await maybeDelay();
      maybeThrow();
      sendAndConfirmCallCount++;
      lastSentTransaction = transaction;
      return {
        signature: transactionSignature,
        executed: !transactionFails,
        err: transactionFails ? transactionError : undefined,
      };
    },

    // Add other common RPC methods as needed
    getTransaction: async (): Promise<unknown> => {
      await maybeDelay();
      maybeThrow();
      return { signature: transactionSignature, slot: 12345 };
    },

    getLatestBlockhash: async (): Promise<{ blockhash: string; lastValidBlockHeight: number }> => {
      await maybeDelay();
      maybeThrow();
      return {
        blockhash: "MockBlockhash123456789ABCDEFGHIJKLMNOPQRSTUV",
        lastValidBlockHeight: 100000,
      };
    },

    // Test helpers
    _setBalance: (address: string, balance: bigint) => {
      balances.set(address, balance);
    },

    get _sendTransactionCallCount(): number {
      return sendTransactionCallCount;
    },

    get _sendAndConfirmCallCount(): number {
      return sendAndConfirmCallCount;
    },

    get _lastSentTransaction(): Uint8Array | null {
      return lastSentTransaction;
    },

    _setTransactionFails: (fails: boolean, err?: string) => {
      transactionFails = fails;
      transactionError = err;
    },

    _reset: () => {
      sendTransactionCallCount = 0;
      sendAndConfirmCallCount = 0;
      lastSentTransaction = null;
      transactionFails = false;
      transactionError = undefined;
      balances.clear();
    },
  } as MockRialoClient;

  return client;
}

// =============================================================================
// Mock Storage
// =============================================================================

/**
 * Creates an in-memory Storage implementation for testing.
 */
export function createMockStorage(): Storage {
  const store = new Map<string, string>();

  return {
    get length(): number {
      return store.size;
    },

    key(index: number): string | null {
      const keys = Array.from(store.keys());
      return keys[index] ?? null;
    },

    getItem(key: string): string | null {
      return store.get(key) ?? null;
    },

    setItem(key: string, value: string): void {
      store.set(key, value);
    },

    removeItem(key: string): void {
      store.delete(key);
    },

    clear(): void {
      store.clear();
    },
  };
}

// =============================================================================
// Mock Config Factory
// =============================================================================

export interface CreateMockConfigOptions {
  /** Initial state overrides */
  initialState?: Partial<FrostAppState>;
  /** Mock client options */
  clientOptions?: CreateMockClientOptions;
  /** Mock storage (null to disable persistence) */
  storage?: Storage | null;
  /** Chain ID */
  chainId?: string;
  /** Auto-connect setting */
  autoConnect?: boolean;
}

/**
 * Creates a mock FrostConfig for testing.
 *
 * @example
 * ```ts
 * const { config, mockClient } = createMockConfig({
 *   initialState: { status: "connected", walletName: "TestWallet" },
 * });
 *
 * // Use in tests
 * const result = await connect(config, { walletName: "TestWallet" });
 * expect(mockClient._connectCallCount).toBe(1);
 * ```
 */
export function createMockConfig(options: CreateMockConfigOptions = {}): {
  config: FrostConfig;
  mockClient: MockRialoClient;
  mockStorage: Storage | null;
} {
  const chainId = options.chainId ?? "rialo:devnet";
  const mockClient = createMockClient(options.clientOptions);
  const mockStorage = options.storage === null ? null : (options.storage ?? createMockStorage());

  const initialState: FrostAppState = {
    status: "disconnected",
    chainId,
    walletName: null,
    accountAddress: null,
    connectedAt: null,
    sessionExpiresAt: null,
    lastUsedAt: Date.now(),
    userDisconnected: false,
    wallets: new Map(),
    accounts: new Map(),
    _walletRefs: new Map(),
    ...options.initialState,
  };

  const store = new Store<FrostAppState>(initialState);

  const config: FrostConfig = {
    store,
    // Cast to unknown first since MockRialoClient is a test-only partial implementation
    client: mockClient as unknown as FrostConfig["client"],
    getChainId: () => store.state.chainId,
    switchChain: () => {
      // No-op for tests, or implement if needed
    },
    _registry: null,
    _bridge: null,
    _persistence: null,
    options: {
      clientConfig: {} as never,
      autoConnect: options.autoConnect ?? false,
      sessionTTL: 7 * 24 * 60 * 60 * 1000,
      storageKey: "rialo-frost-test",
      storage: mockStorage,
    },
    destroy: () => {
      // Cleanup
    },
  };

  return { config, mockClient, mockStorage };
}

// =============================================================================
// Test Helpers
// =============================================================================

/**
 * Waits for a condition to be true.
 */
export async function waitFor(
  condition: () => boolean | Promise<boolean>,
  options: { timeout?: number; interval?: number } = {}
): Promise<void> {
  const { timeout = 5000, interval = 50 } = options;
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    if (await condition()) {
      return;
    }
    await new Promise(resolve => setTimeout(resolve, interval));
  }

  throw new Error(`waitFor timed out after ${timeout}ms`);
}

/**
 * Waits for state to match a condition.
 */
export async function waitForState(
  config: FrostConfig,
  predicate: (state: FrostAppState) => boolean,
  options?: { timeout?: number; interval?: number }
): Promise<FrostAppState> {
  await waitFor(() => predicate(config.store.state), options);
  return config.store.state;
}

/**
 * Creates a mock WalletEntity for store initialization.
 */
export function createMockWalletEntity(overrides: Partial<WalletEntity> = {}): WalletEntity {
  return {
    name: "MockWallet",
    icon: "data:image/svg+xml,<svg/>",
    chains: ["rialo:devnet" as IdentifierString],
    features: [
      "standard:connect",
      "standard:disconnect",
      "rialo:signMessage",
      "rialo:signTransaction",
    ],
    installedVersion: "1.0.0",
    priority: 0,
    lastConnectedAt: undefined,
    ...overrides,
  };
}

/**
 * Creates a mock AccountEntity for store initialization.
 */
export function createMockAccountEntity(overrides: Partial<AccountEntity> = {}): AccountEntity {
  return {
    address: "MockAddress123456789ABCDEFGHIJKLMNOPQRSTUV",
    publicKey: new Uint8Array(32).fill(1),
    walletName: "MockWallet",
    chains: ["rialo:devnet" as IdentifierString],
    label: undefined,
    _raw: undefined,
    ...overrides,
  };
}
